
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * TokensGetParams struct
 */
class TokensGetParams {
  /**
   * Initialize struct
   * @param {!number=} login
   * @param {!boolean=} isManager
   * @constructor
   */
  constructor (login = new UInt64(0, 0), isManager = false) {
    this.Login = login
    this.IsManager = isManager
  }

  /**
   * Copy struct (shallow copy)
   * @this {!TokensGetParams}
   * @param {!TokensGetParams} other Other struct
   * @returns {!TokensGetParams} This struct
   */
  copy (other) {
    if (other.Login != null) {
      this.Login = UInt64.fromNumber(other.Login)
    } else {
      this.Login = null
    }
    if (other.IsManager != null) {
      this.IsManager = other.IsManager
    } else {
      this.IsManager = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!TokensGetParams}
   * @returns {!TokensGetParams} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TokensGetParamsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TokensGetParamsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!TokensGetParams}
   * @param {!TokensGetParams} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TokensGetParams)) {
      throw new TypeError('Instance of TokensGetParams is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!TokensGetParams}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Login: ((this.Login != null) ? this.Login.toNumber() : null),
      IsManager: ((this.IsManager != null) ? this.IsManager : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return TokensGetParams.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!TokensGetParams} other Object value
   * @returns {!TokensGetParams} Created struct
   */
  static fromObject (other) {
    return new TokensGetParams().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!TokensGetParams}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return TokensGetParams.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!TokensGetParams}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 14117
  }
}

export { TokensGetParams };

/**
 * Fast Binary Encoding TokensGetParams field model
 */
class FieldModelTokensGetParams extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Login = new fbe.FieldModelUInt64(buffer, 4 + 4)
    this.IsManager = new fbe.FieldModelBool(buffer, this.Login.fbeOffset + this.Login.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelTokensGetParams}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTokensGetParams}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Login.fbeSize + this.IsManager.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTokensGetParams}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Login.fbeExtra + this.IsManager.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTokensGetParams}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTokensGetParams.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTokensGetParams}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 14117
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTokensGetParams}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTokensGetParams}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Login.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Login.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Login.fbeSize

    if ((fbeCurrentSize + this.IsManager.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.IsManager.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IsManager.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTokensGetParams}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTokensGetParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTokensGetParams}
   * @param {!TokensGetParams} fbeValue Default value, defaults is new TokensGetParams()
   * @returns {!TokensGetParams} TokensGetParams value
   */
  get (fbeValue = new TokensGetParams()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTokensGetParams}
   * @param {!TokensGetParams} fbeValue TokensGetParams value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Login.fbeSize) <= fbeStructSize) {
      fbeValue.Login = this.Login.get()
    } else {
      fbeValue.Login = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Login.fbeSize

    if ((fbeCurrentSize + this.IsManager.fbeSize) <= fbeStructSize) {
      fbeValue.IsManager = this.IsManager.get()
    } else {
      fbeValue.IsManager = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IsManager.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTokensGetParams}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTokensGetParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTokensGetParams}
   * @param {!TokensGetParams} fbeValue TokensGetParams value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTokensGetParams}
   * @param {TokensGetParams} fbeValue TokensGetParams value
   */
  setFields (fbeValue) {
    this.Login.set(fbeValue.Login)
    this.IsManager.set(fbeValue.IsManager)
  }
}

export { FieldModelTokensGetParams };

/**
 * Fast Binary Encoding TokensGetParams model
 */
class TokensGetParamsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTokensGetParams(this.buffer, 4)
  }

  /**
   * Get the TokensGetParams model
   * @this {!TokensGetParamsModel}
   * @returns {!FieldModelTokensGetParams} model TokensGetParams model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TokensGetParamsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TokensGetParamsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TokensGetParamsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TokensGetParamsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTokensGetParams.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TokensGetParamsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TokensGetParamsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TokensGetParamsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TokensGetParamsModel}
   * @param {!TokensGetParams} value TokensGetParams value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TokensGetParamsModel}
   * @param {!TokensGetParams} value TokensGetParams value, defaults is new TokensGetParams()
   * @return {!object} Deserialized TokensGetParams value and its size
   */
  deserialize (value = new TokensGetParams()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new TokensGetParams(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new TokensGetParams(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TokensGetParamsModel}
   * @param {!number} prev Previous TokensGetParams model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TokensGetParamsModel };
